import React, { useState } from 'react';
import { 
  Package, 
  ArrowLeftRight, 
  ClipboardCheck, 
  FileOutput, 
  Settings, 
  LogOut, 
  RefreshCw, 
  Menu, 
  X,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useStore, setState } from '../../hooks/useStore.js';
import { syncWithSupabase } from '../../services/sync.js';

export function Header({ user, onSignOut, realtimeConnected, syncStatusText }) {
  const activeTab = useStore(s => s.activeTab);
  const isSyncing = useStore(s => s.cloudSyncInProgress);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  const tabs = [
    { id: 'inventory', label: 'คลังสินค้า', icon: Package },
    { id: 'transactions', label: 'ยืม-คืน', icon: ArrowLeftRight },
    { id: 'audit', label: 'ตรวจนับสต็อก', icon: ClipboardCheck },
    { id: 'stock-out', label: 'ประวัติจ่ายออก', icon: FileOutput },
    { id: 'settings', label: 'ตั้งค่า', icon: Settings },
  ];

  const handleTabClick = (tabId) => {
    setState({ activeTab: tabId });
    setMobileMenuOpen(false);
  };

  const handleManualSync = async () => {
    if (isSyncing) return;
    try {
      await syncWithSupabase({ forcePull: true });
    } catch (e) {
      console.warn('Manual sync trigger note:', e);
    }
  };

  const userPhoto = user?.user_metadata?.avatar_url || user?.user_metadata?.picture || '';
  const userName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || 'สมาชิกชมรม';

  return (
    <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-slate-200/80 shadow-xs">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-5 lg:px-6 h-16 flex items-center justify-between gap-2 sm:gap-3">
        {/* Left: Mobile Toggle & Brand */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 active:scale-95 transition-all"
            aria-label="เมนู"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <div 
            onClick={() => handleTabClick('inventory')}
            className="flex items-center gap-2.5 cursor-pointer select-none group"
          >
            <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center overflow-hidden shadow-xs group-hover:scale-105 transition-transform">
              <img
                src="sdr-logo.png"
                alt="SDR Logo"
                className="w-7 h-7 object-contain"
                onError={(e) => {
                  if (!e.currentTarget.dataset.fallback) {
                    e.currentTarget.dataset.fallback = '1';
                    e.currentTarget.src = 'assets/sdr-logo.png';
                  } else {
                    e.currentTarget.src = 'sdr-logo-favicon.png';
                  }
                }}
              />
            </div>
            <div>
              <div className="text-base font-extrabold text-slate-900 leading-tight tracking-tight flex items-center gap-1.5 whitespace-nowrap">
                SDR Stock
                <span className="hidden xl:inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700">Manager</span>
              </div>
              <div className="hidden xl:block text-[11px] text-slate-600 font-semibold leading-none">Mahidol University</div>
            </div>
          </div>
        </div>

        {/* Center: Desktop/Tablet Navigation Tabs */}
        <nav className="hidden md:flex items-center gap-1 lg:gap-1.5 flex-shrink-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-2 lg:px-3 py-1.5 rounded-lg text-xs lg:text-sm font-bold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 select-none ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-700 shadow-xs border border-indigo-200/60'
                    : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 lg:w-4 lg:h-4 flex-shrink-0 ${isActive ? 'text-indigo-600' : 'text-slate-500'}`} />
                <span className="whitespace-nowrap">{tab.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Right: Sync Badge & Profile Dropdown */}
        <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3 flex-shrink-0">
          {/* Sync Status Badge */}
          <button
            onClick={handleManualSync}
            disabled={isSyncing}
            title={isSyncing ? 'กำลังซิงค์ข้อมูล...' : 'คลิกเพื่อรีเฟรชข้อมูลล่าสุดกับคลาวด์'}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all cursor-pointer flex-shrink-0 ${
              isSyncing
                ? 'bg-amber-50 text-amber-700 border-amber-200'
                : realtimeConnected
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100/60'
                : 'bg-slate-100 text-slate-600 border-slate-200'
            }`}
          >
            {isSyncing ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin text-amber-600 flex-shrink-0" />
            ) : realtimeConnected ? (
              <Wifi className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
            ) : (
              <WifiOff className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            )}
            <span className="hidden lg:inline whitespace-nowrap">
              {isSyncing ? 'กำลังซิงค์' : syncStatusText || (realtimeConnected ? 'ออนไลน์' : 'ออฟไลน์')}
            </span>
          </button>

          {/* User Profile */}
          <div className="relative">
            <button
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              className="flex items-center gap-2 p-1 rounded-full hover:ring-2 hover:ring-indigo-300 transition-all cursor-pointer"
              aria-label="บัญชีผู้ใช้"
            >
              {userPhoto && !avatarError ? (
                <img
                  src={userPhoto}
                  alt={userName}
                  referrerPolicy="no-referrer"
                  onError={() => setAvatarError(true)}
                  className="w-8 h-8 rounded-full object-cover border border-slate-200 shadow-xs"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-indigo-600 text-white font-bold text-xs flex items-center justify-center shadow-xs">
                  {userName.charAt(0).toUpperCase()}
                </div>
              )}
            </button>

            {profileDropdownOpen && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setProfileDropdownOpen(false)} 
                />
                <div className="absolute right-0 mt-2 w-64 rounded-xl bg-white border border-slate-200 shadow-xl p-3 z-50 animate-in fade-in zoom-in-95 duration-100">
                  <div className="pb-3 border-b border-slate-100 mb-2">
                    <div className="font-bold text-sm text-slate-900 truncate">{userName}</div>
                    <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                  </div>
                  <button
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      onSignOut();
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>ออกจากระบบ</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Drawer Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-md px-4 py-3 space-y-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-700 font-bold border border-indigo-100'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </header>
  );
}
